import get from 'lodash/get';
import omit from 'lodash/omit';
import mapKeys from 'lodash/mapKeys';
import update from 'immutability-helper';
import createMappedReducer from 'src/common/reducers/utils/createMappedReducer';
import playerStatuses from 'src/common/utils/playerStatuses';
import { isType, types } from 'src/common/utils/guideItemTypes';
import * as PlayerActions from '../actions/player';
import * as PrerollActions from '../actions/preroll';
import * as TunerActions from '../actions/tuner';
import { fulfilled } from './utils/asyncActionNameSuffixes';

function primeTuneForMobileSync(state) {
  return {
    ...state,
    isTunePrimed: true,
  };
}

function requestTune(state, action) {
  const {
    tunedGuideId,
    parentGuideId,
    nowPlaying,
    guideItemPathname,
    itemToken,
    tuneRequestStart,
  } = action;

  return {
    ...state,
    isTunePrimed: false,
    tunedGuideId,
    parentGuideId,
    nowPlaying,
    guideItemPathname,
    playerStatus: playerStatuses.connecting,
    positionInfo: {
      elapsedPercent: 0,
      elapsedSeconds: 0,
      duration: 0,
    },
    itemToken,
    tuneRequestStart,
  };
}

function handleLoad(state) {
  const { tunedGuideId } = state;

  return {
    ...state,
    playerStatus: playerStatuses.loaded,
    canScrub:
      isType(tunedGuideId, types.topic) ||
      isType(tunedGuideId, types.audioClip),
    showStartEnd: true,
    autoPlayReady: false,
  };
}

function handlePrerollLoad(state) {
  return {
    ...state,
    playerStatus: playerStatuses.connecting,
  };
}

function tunerPlaying(state) {
  return {
    ...state,
    playerStatus: playerStatuses.playing,
    streamEvaluationStart: null,
    tuneRequestStart: null,
    hadPreroll: null,
  };
}

function tunerPlayingAd(state) {
  return {
    ...state,
    playerStatus: playerStatuses.playing,
  };
}

function tunerPreroll(state, meta) {
  const updatedState = {
    ...state,
    playerStatus: playerStatuses.preroll,
    hadPreroll: true,
  };
  if (meta.isPausePrerollEnabled) {
    updatedState.canScrub = true;
  }
  return updatedState;
}

function handlePrerollEnd(state) {
  const { tunedGuideId } = state;

  return {
    ...state,
    canScrub:
      isType(tunedGuideId, types.topic) ||
      isType(tunedGuideId, types.audioClip),
  };
}

function tunerPaused(state) {
  return {
    ...state,
    playerStatus: playerStatuses.paused,
  };
}

function tunerStopped(state) {
  return {
    ...state,
    playerStatus: playerStatuses.stopped,
  };
}

function tunerSeek(state, action) {
  const { duration } = state.positionInfo;
  const { positionPercent } = action;

  return {
    ...state,
    positionInfo: {
      ...state.positionInfo,
      duration,
      elapsedPercent: positionPercent,
      elapsedSeconds: positionPercent * duration * 0.01,
    },
  };
}

function tunerSetVolume(state, action) {
  return {
    ...state,
    previousVolume: state.volume,
    volume: action.value,
  };
}

function setShowVolumeBar(state, action) {
  return {
    ...state,
    showVolumeBar: action.value,
  };
}

function setHasAdBlocker(state, action) {
  return {
    ...state,
    hasAdBlocker: action.hasAdBlocker,
  };
}

function handleStartStreamEvaluation(state, action) {
  return {
    ...state,
    streamEvaluationStart: action.streamEvaluationStart,
  };
}

function tunerConnecting(state, action) {
  return {
    ...state,
    tunedGuideId: action.tunedGuideId,
    playerStatus: playerStatuses.connecting,
  };
}

function handleMeta(state, { meta = {} }) {
  const { title, image } = meta;
  const nowPlaying = {};
  const stationImage = state?.nowPlaying?.stationImage;
  if (title) {
    nowPlaying.title = title;
  }
  // If available, use album image
  nowPlaying.image = image || stationImage;
  return update(state, {
    nowPlaying: {
      $merge: nowPlaying,
    },
  });
}

function handlePlayerSelected(state, { playerName }) {
  return update(state, {
    playerNameList: { $push: [playerName] },
  });
}

function handleClearPlayerNameList(state) {
  return {
    ...state,
    playerNameList: [],
  };
}

function handleNowPlayingChanged(state, action) {
  // NOTE: This action obj can look slightly different when coming from web-tuner vs. web-api-client
  const nowPlaying =
    get(action, 'nowPlaying') || get(action, 'payload.nowPlaying');

  // to merge share
  const mergedShare = update(state.nowPlaying.share || {}, {
    $merge: nowPlaying.share,
  });

  // to merge description
  const mergedNowPlaying = update(state.nowPlaying, {
    $merge: nowPlaying,
    image: { $set: nowPlaying.image },
    stationImage: { $set: nowPlaying.image },
    share: { $set: mergedShare },
  });

  const followOptions = mapKeys(
    nowPlaying.followOptions,
    ({ guideId }) => guideId,
  );

  return update(state, {
    followOptions: { $set: followOptions },
    nowPlaying: { $set: omit(mergedNowPlaying, 'followOptions') },
  });
}

function tunerPositionChanged(state, action) {
  const { positionInfo, isMobile } = action;
  const canScrub = isMobile
    ? state.canScrub
    : get(positionInfo, 'isLiveSeekStream', state.canScrub);

  return {
    ...state,
    positionInfo,
    canScrub,
  };
}

function mediaEnded(state) {
  return {
    ...state,
    playerStatus: playerStatuses.paused,
    positionInfo: {
      elapsedPercent: 0,
      elapsedSeconds: 0,
    },
  };
}

function tunerWaiting(state) {
  return {
    ...state,
    playerStatus: playerStatuses.connecting,
  };
}

function tunerFailed(state) {
  return {
    ...state,
    playerStatus: playerStatuses.failed,
    playerNameList: [],
    streamEvaluationStart: null,
    tuneRequestStart: null,
    hadPreroll: null,
  };
}

function handleHtmlStream(state, action) {
  return {
    ...state,
    playerStatus: playerStatuses.popout,
    externalHtmlPlayerUrl: action.externalHtmlPlayerUrl,
  };
}

function nextTune(state, action) {
  return {
    ...state,
    playerNameList: [],
    itemToken: action.itemToken,
    guideItemPathname: action.guideItemPathname || state.guideItemPathname,
  };
}

function setPopoutPlayerStatus(state) {
  return update(state, {
    playerStatus: { $set: playerStatuses.popout },
    externalHtmlPlayerUrl: { $set: null },
  });
}

function handlePlayerRateChange(state, { rate, canChangePlaybackRate }) {
  return update(state, {
    playbackRate: { $set: rate },
    canChangePlaybackRate: { $set: canChangePlaybackRate },
  });
}

function handleAutoPlay(state, action) {
  const guideItem = get(action, 'meta.guideItem', {});
  const guideItemPathname = get(action, 'meta.guideItemPathname');

  return update(state, {
    isTunePrimed: { $set: false },
    tunedGuideId: { $set: guideItem.guideId },
    playerStatus: { $set: playerStatuses.idle },
    positionInfo: {
      elapsedPercent: { $set: 0 },
      elapsedSeconds: { $set: 0 },
      duration: { $set: 0 },
    },
    autoPlayGuideItem: { $set: guideItem },
    autoPlayReady: { $set: true },
    guideItemPathname: { $set: guideItemPathname },
  });
}

const initialState = {
  playerStatus: playerStatuses.idle,
  isTunePrimed: false,
  nowPlaying: {},
  followOptions: {},
  positionInfo: {},
  volume: 100,
  playbackRate: 1.0,
  canChangePlaybackRate: false,
  previousVolume: 0,
  externalHtmlPlayerUrl: null,
  streamEvaluationStart: null,
  tuneRequestStart: null,
  hadPreroll: null,
  canScrub: false,
  showStartEnd: false,
  showVolumeBar: false,
  autoPlayReady: false,
  autoPlayGuideItem: {},
};

export const player = createMappedReducer(initialState, {
  [fulfilled(PlayerActions.FETCH_NOW_PLAYING)]: handleNowPlayingChanged,
  [PlayerActions.PRE_TUNE]: primeTuneForMobileSync,
  [PlayerActions.REQUEST_TUNE]: requestTune,
  [PlayerActions.HANDLE_LOAD]: handleLoad,
  [PrerollActions.PREROLL_LOAD]: handlePrerollLoad,
  [PrerollActions.PREROLL]: tunerPreroll,
  [PrerollActions.HIDE_PREROLL_PLAYER]: handlePrerollEnd,
  [PlayerActions.PAUSE]: tunerPaused,
  [PlayerActions.STOP]: tunerStopped,
  [PlayerActions.SEEK]: tunerSeek,
  [PlayerActions.SET_VOLUME]: tunerSetVolume,
  [PlayerActions.SET_HAS_ADBLOCKER]: setHasAdBlocker,
  [PlayerActions.SET_SHOW_VOLUME_BAR]: setShowVolumeBar,
  [PlayerActions.SET_POPOUT_PLAYER_STATUS]: setPopoutPlayerStatus,
  [PlayerActions.AUTOPLAY_READY]: handleAutoPlay,
  [TunerActions.CHANGE_PLAYBACK_RATE]: handlePlayerRateChange,
  [TunerActions.START_STREAM_EVALUATION]: handleStartStreamEvaluation,
  [TunerActions.PLAYING]: tunerPlaying,
  [TunerActions.PLAYING_AD]: tunerPlayingAd,
  [TunerActions.CONNECTING]: tunerConnecting,
  [TunerActions.NOW_PLAYING_CHANGED]: handleNowPlayingChanged,
  [TunerActions.POSITION_CHANGED]: tunerPositionChanged,
  [TunerActions.MEDIA_ENDED]: mediaEnded,
  [TunerActions.WAITING]: tunerWaiting,
  [TunerActions.FAILED]: tunerFailed,
  [TunerActions.HANDLE_HTML_STREAM]: handleHtmlStream,
  [TunerActions.NEXT_TUNE]: nextTune,
  [TunerActions.HANDLE_META]: handleMeta,
  [TunerActions.PLAYER_SELECTED]: handlePlayerSelected,
  [TunerActions.CLEAR_PLAYER_NAME_LIST]: handleClearPlayerNameList,
});
